'use strict'

const ALLOWED_TAGS = new Set([
  'p', 'br', 'hr', 'strong', 'em', 'b', 'i', 'u', 's', 'ul', 'ol', 'li',
  'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'span', 'div',
  'table', 'thead', 'tbody', 'tr', 'th', 'td', 'img', 'pre', 'code'
])

const DROP_WITH_CONTENTS = new Set(['script', 'style', 'iframe', 'object', 'embed', 'svg', 'math', 'template', 'noscript'])

const VOID_TAGS = new Set(['br', 'hr', 'img'])

const URL_ATTRIBUTES = new Set(['href', 'src'])

function escapeHtml (value) {

  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function escapeAttribute (value) {

  return escapeHtml(value)
}

function allowedAttribute (tag, name, value) {

  const attribute = String(name || '').toLowerCase()

  if (!attribute || attribute.startsWith('on') || attribute === 'srcdoc') return false

  if (tag === 'a' && (attribute === 'href' || attribute === 'title' || attribute === 'target')) return true
  if (tag === 'img' && (attribute === 'src' || attribute === 'alt' || attribute === 'title')) return true
  if ((tag === 'td' || tag === 'th') && (attribute === 'colspan' || attribute === 'rowspan')) return true
  if (attribute === 'class' || attribute === 'style') return true
  if ((attribute === 'data-view-id' || attribute === 'data-page-id') && /^[A-Za-z_][A-Za-z0-9_]*$/.test(String(value || ''))) return true

  return false
}

function safeUrl (value) {

  const text = String(value || '').trim().replace(/[\u0000-\u001f\s]+/g, '')

  if (!text) return ''
  if (/^https?:\/\//i.test(text)) return text
  if (/^mailto:/i.test(text)) return text
  if (/^data:image\/(png|jpeg|jpg|gif|webp);base64,/i.test(text)) return text
  // Same-app links such as /myapp/details. Protocol-relative and backslash URLs are not links.
  if (text.startsWith('/') && !text.startsWith('//') && !text.includes('\\') && !text.includes(':')) return text

  return ''
}

const LINK_TARGETS = new Set(['_blank', '_self', '_parent', '_top'])

function safeInlineStyle (value) {

  const kept = []

  for (const part of String(value || '').split(';')) {

    const piece = part.trim()

    if (!piece) continue
    if (/url\s*\(|expression\s*\(|javascript\s*:|@import|-moz-binding|behavior\s*:|\/\*|\\/i.test(piece)) continue

    const match = /^([a-z-]+)\s*:\s*(.+)$/i.exec(piece)

    if (!match) continue

    const prop = match[1].toLowerCase()
    const raw = match[2].trim()

    if (prop === 'font-size' && /^(?:\d{1,3}(?:\.\d+)?(?:px|em|rem|%)|smaller|larger|xx-small|x-small|small|medium|large|x-large|xx-large)$/i.test(raw)) {

      kept.push(`font-size: ${raw.toLowerCase()}`)
      continue
    }

    if (prop === 'font-family' && raw.length <= 120 && /^[A-Za-z0-9 ,.'"\-]+$/.test(raw)) {

      kept.push(`font-family: ${raw}`)
    }
  }

  return kept.join('; ')
}

function parseAttributes (raw) {

  const attributes = []
  const pattern = /([^\s=\/]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+)))?/g
  let match

  while ((match = pattern.exec(raw))) {

    attributes.push({
      name: match[1],
      value: match[2] != null ? match[2] : (match[3] != null ? match[3] : (match[4] != null ? match[4] : ''))
    })
  }

  return attributes
}

function sanitizeHtml (html) {

  const source = String(html == null ? '' : html)
  let output = ''
  let index = 0

  while (index < source.length) {

    if (source.startsWith('<!--', index)) {

      const end = source.indexOf('-->', index + 4)

      index = end === -1 ? source.length : end + 3
      continue
    }

    if (source[index] !== '<') {

      const next = source.indexOf('<', index)
      const text = next === -1 ? source.slice(index) : source.slice(index, next)

      output += escapeHtml(text)
      index = next === -1 ? source.length : next
      continue
    }

    const close = source.indexOf('>', index + 1)

    if (close === -1) {

      output += escapeHtml(source.slice(index))
      break
    }

    const raw = source.slice(index + 1, close).trim()
    index = close + 1

    if (!raw || raw.startsWith('!') || raw.startsWith('?')) continue

    const closing = raw.startsWith('/')
    const body = closing ? raw.slice(1).trim() : raw.replace(/\/$/, '').trim()
    const nameMatch = /^[A-Za-z][A-Za-z0-9]*/.exec(body)
    const tag = nameMatch ? nameMatch[0].toLowerCase() : ''

    if (!tag) continue

    if (!closing && DROP_WITH_CONTENTS.has(tag)) {

      const endTag = new RegExp(`</${tag}\\s*>`, 'i')
      const rest = source.slice(index)
      const found = endTag.exec(rest)

      index = found ? index + found.index + found[0].length : source.length
      continue
    }

    if (!ALLOWED_TAGS.has(tag)) continue
    if (closing) {

      if (!VOID_TAGS.has(tag)) output += `</${tag}>`
      continue
    }

    const attributeSource = body.slice(nameMatch[0].length)
    const kept = []

    for (const attribute of parseAttributes(attributeSource)) {

      if (!allowedAttribute(tag, attribute.name, attribute.value)) continue

      const attributeName = attribute.name.toLowerCase()
      let value = attribute.value

      if (attributeName === 'style') {

        value = safeInlineStyle(value)
        if (!value) continue
      }

      if (attributeName === 'target') {

        const target = String(value || '').trim().toLowerCase()

        if (!LINK_TARGETS.has(target)) continue

        value = target
      }

      if (URL_ATTRIBUTES.has(attributeName)) {

        value = safeUrl(value)
        if (!value) continue
      }

      kept.push(`${attributeName}="${escapeAttribute(value)}"`)
    }

    const attributes = kept.length ? ` ${kept.join(' ')}` : ''

    output += VOID_TAGS.has(tag) ? `<${tag}${attributes}>` : `<${tag}${attributes}>`
  }

  return output
}

function claimsHtml (value) {

  return typeof value === 'string' && /<\/?[a-z][a-z0-9]*\b/i.test(value)
}

function richTextObjectText (value) {

  if (!value || typeof value !== 'object') return null

  if (Array.isArray(value.richText)) {

    return value.richText.map((part) => part && part.text != null ? String(part.text) : '').join('')
  }

  if (value.result != null && typeof value.result !== 'object') return String(value.result)

  if (typeof value.text === 'string' && typeof value.hyperlink === 'string') {

    const href = safeUrl(value.hyperlink)

    return href ? `<a href="${escapeAttribute(href)}">${escapeHtml(value.text)}</a>` : value.text
  }

  if (typeof value.text === 'string') return value.text

  return null
}

function sanitizeRichTextValue (value) {

  if (value == null || value === '') return value
  if (typeof value === 'string') return sanitizeHtml(value)

  if (value && typeof value === 'object') {

    const source = richTextObjectText(value)

    if (source == null) return value

    return sanitizeHtml(source)
  }

  return value
}

// Rich text is always HTML. A text value is HTML when it contains a tag.
// Other column types are left as written.
function sanitizeVisitorRecord (columns, values, merged) {

  if (!values || !Array.isArray(columns)) return values

  for (const column of columns) {

    if (!column || !column.uniqueTitle) continue
    if (!Object.prototype.hasOwnProperty.call(values, column.uniqueTitle)) continue

    const key = column.uniqueTitle
    let next = values[key]

    if (column.type === 'richText') next = sanitizeRichTextValue(values[key])
    else if (column.type === 'text' && claimsHtml(values[key])) next = sanitizeHtml(values[key])
    else continue

    values[key] = next

    if (merged && merged !== values) merged[key] = next
  }

  return values
}

const RICH_TEXT_NESTED = ['components', 'columns', 'rows', 'views']

function sanitizeRichTextViews (components) {

  if (!Array.isArray(components)) return components

  for (const component of components) {

    if (!component || typeof component !== 'object') continue

    if (component.type === 'richtext' && typeof component.richTextContent === 'string') {

      component.richTextContent = sanitizeHtml(component.richTextContent)
    }

    for (const key of RICH_TEXT_NESTED) {

      if (Array.isArray(component[key])) sanitizeRichTextViews(component[key])
    }
  }

  return components
}

function staticRichTextHtml (value, title) {

  const text = typeof value === 'string' ? value.trim() : ''
  const limited = text.slice(0, 8000)

  if (!limited) return `<p>${escapeHtml(title || '')}</p>`

  if (/<[a-z][\s\S]*>/i.test(limited)) {

    const clean = sanitizeHtml(limited)

    return clean || `<p>${escapeHtml(title || '')}</p>`
  }

  return `<p>${escapeHtml(limited)}</p>`
}

function sanitizeStyleText (css) {

  return String(css == null ? '' : css)
    .replace(/<\/style/gi, '')
    .replace(/expression\s*\(/gi, '')
    .replace(/javascript\s*:/gi, '')
    .replace(/@import/gi, '')
    .replace(/-moz-binding\s*:/gi, '')
    .replace(/url\s*\(\s*(['"]?)\s*javascript/gi, 'url($1')
}

module.exports = {
  escapeHtml,
  sanitizeHtml,
  sanitizeStyleText,
  claimsHtml,
  sanitizeRichTextValue,
  sanitizeVisitorRecord,
  sanitizeRichTextViews,
  staticRichTextHtml
}
